import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Copy, Download, Link2, PackageCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  licensingApi,
  type ReleaseDownloadCatalogItemRecord,
  type ReleaseDownloadGrantRecord,
  type IssuedReleaseDownloadGrantRecord,
} from '@/api/licensing';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  buildInstallerCatalog,
  filterInstallerApps,
  releaseCatalogItemLabel,
  type InstallerAppGroup,
  type InstallerPlatformGroup,
} from './releaseDownloadCatalogModel';
import { isMailArchiverApp, MailArchiverIcon } from './MailArchiverIcon';

const TTL_OPTIONS = [
  { value: '24', label: '24 часа' },
  { value: '72', label: '3 дня' },
  { value: '168', label: '7 дней' },
] as const;

const DOWNLOAD_LIMIT_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '5', label: '5' },
  { value: '0', label: 'без лимита' },
] as const;

function formatDateTime(value?: string): string {
  if (!value) return 'нет данных';
  return new Date(value).toLocaleString('ru-RU');
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function grantTone(status: string): 'visit' | 'deadline' | 'outline' {
  if (status === 'ACTIVE') return 'visit';
  if (status === 'REVOKED' || status === 'EXPIRED' || status === 'EXHAUSTED') return 'deadline';
  return 'outline';
}

export function ReleaseDownloadGrantsPanel({
  clientId,
  clientName,
}: {
  clientId: number;
  clientName?: string;
}) {
  const queryClient = useQueryClient();
  const [selectedAppId, setSelectedAppId] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [selectedPlatformKey, setSelectedPlatformKey] = useState('');
  const [selectedVersionArtifactId, setSelectedVersionArtifactId] = useState('');
  const [ttlHours, setTtlHours] = useState('24');
  const [maxDownloads, setMaxDownloads] = useState('2');
  const [note, setNote] = useState('');
  const [issued, setIssued] = useState<IssuedReleaseDownloadGrantRecord | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ReleaseDownloadGrantRecord | null>(null);
  const [revokeReason, setRevokeReason] = useState('Выдана ошибочно');

  const catalog = useQuery({
    queryKey: ['licensing', 'client-release-download-catalog', clientId],
    queryFn: () => licensingApi.listClientReleaseDownloadCatalog(clientId),
  });
  const grants = useQuery({
    queryKey: ['licensing', 'client-release-download-grants', clientId],
    queryFn: () => licensingApi.listClientReleaseDownloadGrants(clientId),
  });

  const items = catalog.data?.items ?? [];
  const installerApps = useMemo(() => buildInstallerCatalog(items), [items]);
  const filteredApps = useMemo(() => filterInstallerApps(installerApps, appSearch), [appSearch, installerApps]);
  const artifactById = useMemo(() => new Map(items.map((item) => [item.artifact.id, item])), [items]);
  const selectedApp = useMemo(
    () => installerApps.find((app) => app.appId === selectedAppId) ?? installerApps[0] ?? null,
    [installerApps, selectedAppId],
  );
  const selectedPlatform = useMemo(
    () => selectedApp?.platforms.find((platform) => platform.key === selectedPlatformKey) ?? selectedApp?.platforms[0] ?? null,
    [selectedApp, selectedPlatformKey],
  );
  const explicitVersion = useMemo(
    () => selectedPlatform?.items.find((item) => item.artifact.id === selectedVersionArtifactId) ?? null,
    [selectedPlatform, selectedVersionArtifactId],
  );
  const selectedArtifact = explicitVersion ?? selectedPlatform?.latest ?? null;
  const effectiveArtifactId = selectedArtifact?.artifact.id ?? '';
  const latestSelected = Boolean(
    selectedArtifact && selectedPlatform && selectedArtifact.artifact.id === selectedPlatform.latest.artifact.id,
  );

  const selectApp = (appId: string) => {
    setSelectedAppId(appId);
    setSelectedPlatformKey('');
    setSelectedVersionArtifactId('');
    setIssued(null);
  };

  const selectPlatform = (platformKeyValue: string) => {
    setSelectedPlatformKey(platformKeyValue);
    setSelectedVersionArtifactId('');
    setIssued(null);
  };

  const createGrant = useMutation({
    mutationFn: () =>
      licensingApi.createClientReleaseDownloadGrant(clientId, {
        artifactId: effectiveArtifactId,
        ttlHours: Number(ttlHours),
        maxDownloads: Math.max(0, Number(maxDownloads) || 0),
        note: note.trim(),
      }),
    onSuccess: (grant) => {
      setIssued(grant);
      void queryClient.invalidateQueries({ queryKey: ['licensing', 'client-release-download-grants', clientId] });
      toast.success('Ссылка создана');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Не удалось создать ссылку');
    },
  });

  const revokeGrant = useMutation({
    mutationFn: ({ grantId, reason }: { grantId: string; reason: string }) =>
      licensingApi.revokeClientReleaseDownloadGrant(clientId, grantId, reason),
    onSuccess: () => {
      setRevokeTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['licensing', 'client-release-download-grants', clientId] });
      toast.success('Ссылка отозвана');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Не удалось отозвать ссылку');
    },
  });

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} скопирована`);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const requestRevoke = (grant: ReleaseDownloadGrantRecord) => {
    setRevokeReason('Выдана ошибочно');
    setRevokeTarget(grant);
  };

  const confirmRevoke = () => {
    if (!revokeTarget || !revokeReason.trim()) return;
    revokeGrant.mutate({ grantId: revokeTarget.id, reason: revokeReason.trim() });
  };

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">Установщики</h2>
          <p className="mt-1 truncate text-sm text-muted-foreground">
            {clientName ?? `Клиент ${clientId}`}: временные ссылки на скачивание
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        {catalog.isLoading && (
          <>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </>
        )}

        {catalog.isError && (
          <div className="rounded-md border border-destructive/30 bg-background p-3 text-sm text-destructive">
            {catalog.error instanceof Error ? catalog.error.message : 'Не удалось загрузить каталог установщиков'}
          </div>
        )}

        {!catalog.isLoading && !catalog.isError && items.length === 0 && (
          <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
            Нет опубликованных установщиков. Их нужно опубликовать в licensing-platform.
          </div>
        )}

        {items.length > 0 && selectedApp && selectedPlatform && selectedArtifact && (
          <>
            <AppSelector
              apps={installerApps}
              filteredApps={filteredApps}
              selectedAppId={selectedApp.appId}
              search={appSearch}
              onSearchChange={setAppSearch}
              onSelect={selectApp}
            />

            <PlatformSelector
              platforms={selectedApp.platforms}
              selectedPlatformKey={selectedPlatform.key}
              onSelect={selectPlatform}
            />

            <ArtifactSummary item={selectedArtifact} latestSelected={latestSelected} />

            <GrantPresetGroup label="Срок действия" options={TTL_OPTIONS} value={ttlHours} onChange={setTtlHours} />
            <GrantPresetGroup
              label="Скачивания"
              options={DOWNLOAD_LIMIT_OPTIONS}
              value={maxDownloads}
              onChange={setMaxDownloads}
            />

            <details className="rounded-md border bg-background p-3">
              <summary className="cursor-pointer text-sm font-medium">Дополнительно</summary>
              <VersionSelector
                platform={selectedPlatform}
                selectedArtifactId={selectedArtifact.artifact.id}
                latestArtifactId={selectedPlatform.latest.artifact.id}
                onSelect={(artifactId) => {
                  setSelectedVersionArtifactId(artifactId === selectedPlatform.latest.artifact.id ? '' : artifactId);
                  setIssued(null);
                }}
              />
            </details>

            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Заметка для аудита"
              className="min-h-20"
            />

            <Button
              type="button"
              disabled={!effectiveArtifactId || createGrant.isPending}
              onClick={() => createGrant.mutate()}
            >
              <Link2 className="h-4 w-4" />
              Создать ссылку
            </Button>
          </>
        )}

        {issued && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-green-950 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-100">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Ссылка готова</p>
                <p className="mt-1 break-all font-mono text-xs">{issued.downloadUrl}</p>
                <p className="mt-2 text-xs opacity-80">Действует до {formatDateTime(issued.expiresAt)}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Скопировать ссылку"
                onClick={() => copy(issued.downloadUrl, 'Ссылка')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium">Последние ссылки</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Обновить ссылки"
            onClick={() => void grants.refetch()}
            disabled={grants.isFetching}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {grants.isLoading && (
          <div className="grid gap-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {grants.isError && (
          <div className="rounded-md border border-destructive/30 bg-background p-3 text-sm text-destructive">
            {grants.error instanceof Error ? grants.error.message : 'Не удалось загрузить ссылки'}
          </div>
        )}

        {!grants.isLoading && !grants.isError && (grants.data?.items.length ?? 0) === 0 && (
          <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
            Для этого клиента еще не создавали временные ссылки.
          </div>
        )}

        {grants.data && grants.data.items.length > 0 && (
          <div className="grid gap-2">
            {grants.data.items.map((grant) => (
              <GrantRow
                key={grant.id}
                grant={grant}
                artifact={artifactById.get(grant.artifactId)}
                onRevoke={() => requestRevoke(grant)}
                revoking={revokeGrant.isPending}
              />
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={Boolean(revokeTarget)} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отозвать ссылку</AlertDialogTitle>
            <AlertDialogDescription>
              После отзыва публичная ссылка перестанет скачивать установщик. Уже выполненные скачивания останутся в аудите.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={revokeReason}
            onChange={(event) => setRevokeReason(event.target.value)}
            placeholder="Причина отзыва"
            className="min-h-24"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeGrant.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={!revokeReason.trim() || revokeGrant.isPending}
              onClick={(event) => {
                event.preventDefault();
                confirmRevoke();
              }}
            >
              Отозвать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function AppSelector({
  apps,
  filteredApps,
  selectedAppId,
  search,
  onSearchChange,
  onSelect,
}: {
  apps: InstallerAppGroup[];
  filteredApps: InstallerAppGroup[];
  selectedAppId: string;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (appId: string) => void;
}) {
  if (apps.length <= 1) {
    const app = apps[0];
    return (
      <div className="rounded-md border bg-background p-3">
        <p className="text-xs font-medium text-muted-foreground">Программа</p>
        <p className="mt-1 flex items-center gap-2 text-sm font-semibold">
          {isMailArchiverApp(app?.appId) && <MailArchiverIcon className="h-5 w-5 [image-rendering:pixelated]" />}
          {app?.label ?? 'Программа'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <Input value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Найти программу" />
      <div className="grid gap-2">
        {filteredApps.map((app) => {
          const selected = app.appId === selectedAppId;
          return (
            <button
              key={app.appId}
              type="button"
              className={cn(
                'rounded-md border bg-background p-3 text-left transition-colors',
                selected && 'border-primary bg-primary/5',
              )}
              onClick={() => onSelect(app.appId)}
            >
              <span className="flex items-center gap-2 text-sm font-semibold">
                {isMailArchiverApp(app.appId) && <MailArchiverIcon className="h-5 w-5 [image-rendering:pixelated]" />}
                {app.label}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {app.platforms.length} платформ{app.platforms.length === 1 ? 'а' : 'ы'}
              </span>
            </button>
          );
        })}
        {filteredApps.length === 0 && (
          <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">Программы не найдены.</div>
        )}
      </div>
    </div>
  );
}

function PlatformSelector({
  platforms,
  selectedPlatformKey,
  onSelect,
}: {
  platforms: InstallerPlatformGroup[];
  selectedPlatformKey: string;
  onSelect: (platformKeyValue: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <p className="text-xs font-medium text-muted-foreground">Платформа</p>
      <div className="grid grid-cols-2 gap-2">
        {platforms.map((platform) => {
          const selected = platform.key === selectedPlatformKey;
          return (
            <button
              key={platform.key}
              type="button"
              className={cn(
                'rounded-md border bg-background px-3 py-2 text-left text-sm font-medium transition-colors',
                selected && 'border-primary bg-primary/5',
              )}
              onClick={() => onSelect(platform.key)}
            >
              {platform.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GrantPresetGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="grid grid-cols-3 gap-2">
        {options.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant={option.value === value ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function VersionSelector({
  platform,
  selectedArtifactId,
  latestArtifactId,
  onSelect,
}: {
  platform: InstallerPlatformGroup;
  selectedArtifactId: string;
  latestArtifactId: string;
  onSelect: (artifactId: string) => void;
}) {
  return (
    <div className="mt-3 grid gap-2">
      <p className="text-xs font-medium text-muted-foreground">Версия</p>
      {platform.items.map((item) => {
        const selected = item.artifact.id === selectedArtifactId;
        const latest = item.artifact.id === latestArtifactId;
        return (
          <button
            key={item.artifact.id}
            type="button"
            className={cn(
              'rounded-md border bg-background p-3 text-left transition-colors',
              selected && 'border-primary bg-primary/5',
            )}
            onClick={() => onSelect(item.artifact.id)}
          >
            <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
              {item.version}
              <Badge variant={item.channel.toLowerCase() === 'stable' ? 'visit' : 'outline'}>{item.channel}</Badge>
              {latest && <Badge variant="secondary">latest</Badge>}
            </span>
            <span className="mt-1 block break-words text-xs text-muted-foreground">
              {item.artifact.filename} · {formatBytes(item.artifact.sizeBytes)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ArtifactSummary({
  item,
  latestSelected,
}: {
  item: ReleaseDownloadCatalogItemRecord;
  latestSelected: boolean;
}) {
  return (
    <div className="rounded-md bg-muted p-3">
      <div className="flex items-start gap-3">
        {isMailArchiverApp(item.appId) ? (
          <MailArchiverIcon className="mt-0.5 h-4 w-4 [image-rendering:pixelated]" />
        ) : (
          <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{releaseCatalogItemLabel(item)}</p>
            {latestSelected && <Badge variant="visit">latest</Badge>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {item.artifact.filename} · {formatBytes(item.artifact.sizeBytes)}
          </p>
        </div>
      </div>
    </div>
  );
}

function GrantRow({
  grant,
  artifact,
  onRevoke,
  revoking,
}: {
  grant: ReleaseDownloadGrantRecord;
  artifact?: ReleaseDownloadCatalogItemRecord;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const canRevoke = grant.effectiveStatus === 'ACTIVE';
  return (
    <div className="rounded-md border bg-background p-3" data-testid="release-download-grant-row" data-grant-id={grant.id}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={grantTone(grant.effectiveStatus)}>{grant.effectiveStatus}</Badge>
            <span className="font-mono text-xs text-muted-foreground">{grant.id.slice(0, 8)}</span>
          </div>
          <p className="mt-2 flex items-center gap-2 text-sm">
            {artifact && isMailArchiverApp(artifact.appId) && (
              <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />
            )}
            {artifact ? releaseCatalogItemLabel(artifact) : `Artifact ${grant.artifactId.slice(0, 8)}`}
          </p>
          <p className="mt-2 text-sm">До {formatDateTime(grant.expiresAt)}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Скачиваний: {grant.downloadCount}
            {grant.maxDownloads > 0 ? `/${grant.maxDownloads}` : ' без лимита'}
          </p>
          {grant.note && <p className="mt-1 break-words text-xs text-muted-foreground">{grant.note}</p>}
        </div>
        {canRevoke && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Отозвать ссылку"
            onClick={onRevoke}
            disabled={revoking}
          >
            <Ban className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
